import {
    Activity,
    AppWindow,
    BarChart3,
    Bell,
    Book,
    BookOpen,
    Boxes,
    Briefcase,
    Building2,
    CalendarClock,
    ChartPie,
    CircuitBoard,
    Cloud,
    CloudCog,
    Code2,
    Container,
    CreditCard,
    Database,
    FileSpreadsheet,
    FileText,
    FolderKanban,
    Gauge,
    GitBranch,
    Globe,
    GraduationCap,
    HardDrive,
    Headphones,
    KeyRound,
    LayoutDashboard,
    LayoutGrid,
    LifeBuoy,
    LineChart,
    Lock,
    Mail,
    MessageSquare,
    MonitorSmartphone,
    Network,
    Package,
    PieChart,
    Receipt,
    Server,
    Settings,
    Shield,
    ShieldCheck,
    ShoppingCart,
    Smartphone,
    Sparkles,
    Tags,
    Ticket,
    TrendingUp,
    UserCheck,
    Users,
    Wallet,
    Workflow,
    Wrench,
    type LucideProps,
} from 'lucide-react';
import type { ComponentType } from 'react';

/**
 * Icônes disponibles pour le catalogue.
 *
 * Le registre est explicite plutôt qu'un `import * as Icons` : cette seconde
 * forme embarque les quinze cents icônes de la bibliothèque dans le bundle,
 * pour la douzaine réellement utilisée. Ajouter une icône ici quand un besoin
 * apparaît est un coût dérisoire face à ce poids.
 */
const ICONS: Record<string, ComponentType<LucideProps>> = {
    Activity,
    AppWindow,
    BarChart3,
    Bell,
    Book,
    BookOpen,
    Boxes,
    Briefcase,
    Building2,
    CalendarClock,
    ChartPie,
    CircuitBoard,
    Cloud,
    CloudCog,
    Code2,
    Container,
    CreditCard,
    Database,
    FileSpreadsheet,
    FileText,
    FolderKanban,
    Gauge,
    GitBranch,
    Globe,
    GraduationCap,
    HardDrive,
    Headphones,
    KeyRound,
    LayoutDashboard,
    LayoutGrid,
    LifeBuoy,
    LineChart,
    Lock,
    Mail,
    MessageSquare,
    MonitorSmartphone,
    Network,
    Package,
    PieChart,
    Receipt,
    Server,
    Settings,
    Shield,
    ShieldCheck,
    ShoppingCart,
    Smartphone,
    Sparkles,
    Tags,
    Ticket,
    TrendingUp,
    UserCheck,
    Users,
    Wallet,
    Workflow,
    Wrench,
};

/** Noms proposés dans l'éditeur du catalogue. */
export const AVAILABLE_ICONS = Object.keys(ICONS).sort();

/* =============================================================================
   Logos des outils externes
   =============================================================================
   Jira, Confluence, GitHub, Factorial et Microsoft Partner Center ne sont pas
   des applications de la maison : le lecteur les reconnaît à leur marque bien
   avant d'avoir lu leur nom. Une icône lucide générique, GitBranch pour la
   forge et Users pour le logiciel de paie, gaspille cette reconnaissance.

   CONVENTION. La colonne `applications.icon` reste une simple chaîne. Une
   valeur préfixée par « logo: » désigne un logo de marque, « logo:github » par
   exemple ; toute autre valeur reste un nom d'icône lucide, exactement comme
   avant. Les deux registres ne peuvent donc pas se marcher dessus, et un nom
   inconnu, préfixé ou non, retombe sur AppWindow.

   TRACÉS EMBARQUÉS, et non servis depuis /public. Le composant rend une icône à
   huit endroits de l'application, à six tailles, souvent dans des listes de
   vingt lignes : un fichier par logo, ce sont autant de requêtes et un
   clignotement au premier rendu, pour un gain nul sur un dessin qui pèse
   quelques centaines d'octets. Surtout, un SVG chargé en `<img>` ne peut pas
   hériter de la couleur du texte, ce dont dépend tout ce qui suit. Rien n'est
   chargé depuis l'extérieur, l'application est interne.

   MONOCHROME, et non en couleurs de marque. Les deux supports du catalogue
   posent le glyphe en `currentColor` : blanc sur la pastille pleine de la carte
   (`.app-card-icon`), teinte de l'application sur la pastille diluée des listes
   et de la fiche (`.app-swatch`), et désaturé quand l'accès n'est pas accordé.
   Un logo multicolore ne survivrait à aucun des trois, et cinq cartes à fond
   neutre au milieu de dix-huit pastilles de couleur se liraient comme un défaut
   d'affichage, pas comme une intention. Les éditeurs publient d'ailleurs tous
   une version un ton de leur marque : le monochrome est ici la reproduction
   officielle, pas une dégradation.
   ========================================================================== */

/**
 * Préfixe des valeurs de `applications.icon` qui désignent un logo de marque.
 *
 * Les deux-points sont volontaires : aucune icône lucide n'en porte, la
 * distinction est donc sans ambiguïté possible.
 */
export const LOGO_PREFIX = 'logo:';

interface BrandLogo {
    /** Nom de la marque, pour l'étiquette du sélecteur d'icônes. */
    label: string;
    /** Côté du carré de dessin natif, tel que publié par la marque. */
    box: number;
    /** Tracés du logo officiel, repris sans retouche. */
    paths: string[];
}

/**
 * Logos de marque.
 *
 * Chaque tracé vient de l'actif officiel de l'éditeur, sans redessin : les
 * marques sont déposées, une reproduction approximative serait autant un défaut
 * de goût qu'un problème juridique. La provenance est notée à chaque entrée
 * pour qu'une mise à jour puisse être vérifiée.
 */
const LOGOS: Record<string, BrandLogo> = {
    // Microsoft, marque Azure. Trace Simple Icons (CC0), boite 24.
    azure: {
        label: 'Microsoft Azure',
        box: 24,
        paths: [
            'M22.379 23.343a1.62 1.62 0 0 0 1.536-2.14v.002L17.35 1.76A1.62 1.62 0 0 0 15.816.657H8.184A1.62 1.62 0 0 0 6.65 1.76L.086 21.204a1.62 1.62 0 0 0 1.536 2.139h4.741a1.62 1.62 0 0 0 1.535-1.103l.977-2.892 4.947 3.675c.28.208.618.32.966.32m-3.084-12.531 3.624 10.739a.54.54 0 0 1-.51.713v-.001h-.03a.54.54 0 0 1-.322-.106l-9.287-6.9h4.853m6.313 7.006c.116-.326.13-.694.007-1.058L9.79 1.76a1.722 1.722 0 0 0-.007-.02h6.034a.54.54 0 0 1 .512.366l6.562 19.445a.54.54 0 0 1-.338.684',
        ],
    },

    // Anthropic, marque Claude. Trace Simple Icons (CC0), boite 24.
    claude: {
        label: 'Claude',
        box: 24,
        paths: [
            'm4.7144 15.9555 4.7174-2.6471.079-.2307-.079-.1275h-.2307l-.7893-.0486-2.6956-.0729-2.3375-.0971-2.2646-.1214-.5707-.1215-.5343-.7042.0546-.3522.4797-.3218.686.0608 1.5179.1032 2.2767.1578 1.6514.0972 2.4468.255h.3886l.0546-.1579-.1336-.0971-.1032-.0972L6.973 9.8356l-2.55-1.6879-1.3356-.9714-.7225-.4918-.3643-.4614-.1578-1.0078.6557-.7225.8803.0607.2246.0607.8925.686 1.9064 1.4754 2.4893 1.8336.3643.3035.1457-.1032.0182-.0728-.164-.2733-1.3539-2.4467-1.445-2.4893-.6435-1.032-.17-.6194c-.0607-.255-.1032-.4674-.1032-.7285L6.287.1335 6.6997 0l.9957.1336.419.3642.6192 1.4147 1.0018 2.2282 1.5543 3.0296.4553.8985.2429.8318.091.255h.1579v-.1457l.1275-1.706.2368-2.0947.2307-2.6957.0789-.7589.3764-.9107.7468-.4918.5828.2793.4797.686-.0668.4433-.2853 1.8517-.5586 2.9021-.3643 1.9429h.2125l.2429-.2429.9835-1.3053 1.6514-2.0643.7286-.8196.85-.9046.5464-.4311h1.0321l.759 1.1293-.34 1.1657-1.0625 1.3478-.8804 1.1414-1.2628 1.7-.7893 1.36.0729.1093.1882-.0183 2.8535-.607 1.5421-.2794 1.8396-.3157.8318.3886.091.3946-.3278.8075-1.967.4857-2.3072.4614-3.4364.8136-.0425.0304.0486.0607 1.5482.1457.6618.0364h1.621l3.0175.2247.7892.522.4736.6376-.079.4857-1.2142.6193-1.6393-.3886-3.825-.9107-1.3113-.3279h-.1822v.1093l1.0929 1.0686 2.0035 1.8092 2.5075 2.3314.1275.5768-.3218.4554-.34-.0486-2.2039-1.6575-.85-.7468-1.9246-1.621h-.1275v.17l.4432.6496 2.3436 3.5214.1214 1.0807-.17.3521-.6071.2125-.6679-.1214-1.3721-1.9246L14.38 17.959l-1.1414-1.9428-.1397.079-.674 7.2552-.3156.3703-.7286.2793-.6071-.4614-.3218-.7468.3218-1.4753.3886-1.9246.3157-1.53.2853-1.9004.17-.6314-.0121-.0425-.1397.0182-1.4328 1.9672-2.1796 2.9446-1.7243 1.8456-.4128.164-.7164-.3704.0667-.6618.4008-.5889 2.386-3.0357 1.4389-1.882.929-1.0868-.0062-.1579h-.0546l-6.3385 4.1164-1.1293.1457-.4857-.4554.0608-.7467.2307-.2429 1.9064-1.3114Z',
        ],
    },

    // Atlassian, marque Confluence. Tracé Simple Icons (CC0), boîte 24.
    confluence: {
        label: 'Confluence',
        box: 24,
        paths: [
            'M.87 18.257c-.248.382-.53.875-.763 1.245a.764.764 0 0 0 .255 1.04l4.965 3.054a.764.764 0 0 0 1.058-.26c.199-.332.454-.763.733-1.221 1.967-3.247 3.945-2.853 7.508-1.146l4.957 2.337a.764.764 0 0 0 1.028-.382l2.364-5.346a.764.764 0 0 0-.382-1 599.851 599.851 0 0 1-4.965-2.361C10.911 10.97 5.224 11.185.87 18.257zM23.131 5.743c.249-.405.531-.875.764-1.25a.764.764 0 0 0-.256-1.034L18.675.404a.764.764 0 0 0-1.058.26c-.195.335-.451.763-.734 1.225-1.966 3.246-3.945 2.85-7.508 1.146L4.437.694a.764.764 0 0 0-1.027.382L1.046 6.422a.764.764 0 0 0 .382 1c1.039.49 3.105 1.467 4.965 2.361 6.698 3.246 12.392 3.029 16.738-4.04z',
        ],
    },

    // Factorial. Isotype repris de l'en-tête de factorialhr.com, boîte 56.
    // La marque est publiée en un seul ton, d'où les trois tracés pleins.
    factorial: {
        label: 'Factorial',
        box: 56,
        paths: [
            'M45.6301 49.7538C40.8168 53.6596 34.6817 56 28 56C21.3183 56 15.1832 53.6596 10.3699 49.7538C15.1832 45.8481 21.3183 43.5077 28 43.5077C34.6817 43.5077 40.8168 45.8481 45.6301 49.7538Z',
            'M10.2163 42.9955C6.79849 38.9463 4.73846 33.7137 4.73846 28C4.73846 15.153 15.153 4.73846 28 4.73846C40.847 4.73846 51.2615 15.153 51.2615 28C51.2615 33.7137 49.2015 38.9463 45.7837 42.9955C46.9596 43.7302 48.0866 44.5355 49.1587 45.4053L49.627 45.7854C53.6089 40.949 56 34.7537 56 28C56 12.536 43.464 0 28 0C12.536 0 0 12.536 0 28C0 34.7537 2.39114 40.949 6.37303 45.7853L6.84133 45.4053C7.91335 44.5355 9.04039 43.7302 10.2163 42.9955Z',
            'M38.3385 27.1385C38.3385 32.8482 33.7098 37.4769 28 37.4769C22.2902 37.4769 17.6615 32.8482 17.6615 27.1385C17.6615 21.4287 22.2902 16.8 28 16.8C33.7098 16.8 38.3385 21.4287 38.3385 27.1385Z',
        ],
    },

    // Microsoft, marque Dynamics 365. Trace Simple Icons (CC0), boite 24.
    dynamics: {
        label: 'Dynamics 365',
        box: 24,
        paths: [
            'M15.805 11.322v4.889a2.536 2.536 0 0 1-1.643 2.374l-1.732.652a.507.507 0 0 1-.686-.475v-5.956l-3.392 1.239a1.015 1.015 0 0 0-.664.953v7.986c0 .705.7 1.195 1.363.953l10.161-3.713a2.535 2.535 0 0 0 1.666-2.382V7.696a2.537 2.537 0 0 1-1.666 2.381l-3.407 1.245Zm0-.532V9.323a2.537 2.537 0 0 0-1.645-2.375l-1.728-.65a.508.508 0 0 0-.686.475v4.59c0 .701-.695 1.191-1.355.956L3.795 9.963a1.015 1.015 0 0 1-.674-.956V1.015c0-.701.695-1.191 1.356-.955l14.718 5.256A2.538 2.538 0 0 1 20.83 7.21c-.136.861-1.05 2.128-1.79 2.398l-3.235 1.182Z',
        ],
    },

    // GitHub, marque Octocat. Tracé Simple Icons (CC0), boîte 24.
    github: {
        label: 'GitHub',
        box: 24,
        paths: [
            'M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12',
        ],
    },

    // Atlassian, marque Jira. Tracé Simple Icons (CC0), boîte 24.
    jira: {
        label: 'Jira',
        box: 24,
        paths: [
            'M11.571 11.513H0a5.218 5.218 0 0 0 5.232 5.215h2.13v2.057A5.215 5.215 0 0 0 12.575 24V12.518a1.005 1.005 0 0 0-1.005-1.005zm5.723-5.756H5.736a5.215 5.215 0 0 0 5.215 5.214h2.129v2.058a5.218 5.218 0 0 0 5.215 5.214V6.758a1.001 1.001 0 0 0-1.001-1.001zM23.013 0H11.455a5.215 5.215 0 0 0 5.215 5.215h2.129v2.057A5.215 5.215 0 0 0 24 12.483V1.005A1.001 1.001 0 0 0 23.013 0Z',
        ],
    },

    // Microsoft, employé pour Partner Center qui n'a pas de marque propre.
    // Quatre carrés égaux ; la proportion officielle est un intervalle de 0,104
    // côté, soit 11,407 et 1,186 sur une boîte de 24.
    microsoft: {
        label: 'Microsoft',
        box: 24,
        paths: [
            'M0 0h11.407v11.407H0z',
            'M12.593 0H24v11.407H12.593z',
            'M0 12.593h11.407V24H0z',
            'M12.593 12.593H24V24H12.593z',
        ],
    },
};

/**
 * Valeurs de logo proposées dans l'éditeur du catalogue.
 *
 * Ce sont les chaînes complètes, telles qu'elles doivent être écrites dans
 * `applications.icon` : un sélecteur peut les enregistrer sans rien préfixer.
 */
export const AVAILABLE_LOGOS = Object.keys(LOGOS)
    .sort()
    .map((key) => LOGO_PREFIX + key);

/** Nom de marque de chaque logo, indexé par la valeur stockée en base. */
export const LOGO_LABELS: Record<string, string> = Object.fromEntries(
    Object.entries(LOGOS).map(([key, logo]) => [LOGO_PREFIX + key, logo.label])
);

/**
 * Marge ajoutée autour du dessin de marque, en fraction de son côté.
 *
 * Les tracés officiels remplissent leur carré jusqu'au bord, là où une icône
 * lucide garde deux unités de blanc sur vingt-quatre. Sans correction, un logo
 * paraîtrait donc un cinquième plus gros que ses voisins de la même grille, et
 * il pèse déjà plus lourd à l'œil : un aplat plein noircit davantage qu'un
 * trait de deux unités sur le même encombrement. La valeur est réglée à l'œil,
 * pastilles côte à côte : à 0,14 le dessin occupe 78 % de la boîte, contre 83 %
 * pour une icône lucide, et les deux familles se tiennent enfin.
 */
const LOGO_PADDING = 0.14;

interface BrandGlyphProps extends Omit<LucideProps, 'ref'> {
    logo: BrandLogo;
}

/**
 * Rend un logo de marque avec la même interface qu'une icône lucide : `size`,
 * `className` et les attributs SVG usuels s'y appliquent de la même façon.
 *
 * `size` et `absoluteStrokeWidth` sont retirés du reste des propriétés, le
 * premier parce qu'il pilote ici les dimensions, le second parce qu'il n'a
 * aucun sens sur un dessin plein et que le DOM le refuserait.
 */
function BrandGlyph({
    logo,
    size = 24,
    color,
    absoluteStrokeWidth: _absoluteStrokeWidth,
    ...props
}: BrandGlyphProps) {
    const marge = logo.box * LOGO_PADDING;
    const cote = logo.box + marge * 2;

    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width={size}
            height={size}
            viewBox={`${-marge} ${-marge} ${cote} ${cote}`}
            fill={color ?? 'currentColor'}
            {...props}
        >
            {logo.paths.map((d) => (
                <path key={d} d={d} />
            ))}
        </svg>
    );
}

interface AppIconProps extends Omit<LucideProps, 'ref'> {
    /**
     * Nom d'icône, tel que stocké en base : soit un nom lucide, « BarChart3 »,
     * soit un logo de marque préfixé, « logo:github ».
     */
    name: string;
}

/**
 * Rend une icône du registre, ou le logo d'un outil externe.
 *
 * Le catalogue étant éditable par un administrateur, un nom inconnu peut
 * arriver en base : on retombe sur une icône générique plutôt que de casser le
 * rendu de toute la grille. Le repli couvre aussi bien un nom lucide disparu
 * qu'un logo mal orthographié, et `Object.hasOwn` évite qu'une valeur comme
 * « constructor » ne remonte un membre du prototype d'Object en guise de
 * composant.
 */
export function AppIcon({ name, ...props }: AppIconProps) {
    if (name?.startsWith(LOGO_PREFIX)) {
        const cle = name.slice(LOGO_PREFIX.length);
        if (Object.hasOwn(LOGOS, cle)) {
            return <BrandGlyph logo={LOGOS[cle]} {...props} />;
        }
    }

    const Component = Object.hasOwn(ICONS, name) ? ICONS[name] : AppWindow;
    return <Component {...props} />;
}
